import * as React from "react";
import { makeStyles, Text, Button, Input } from "@fluentui/react-components";
import {
  Send24Filled,
  Delete24Regular,
  Dismiss16Regular,
} from "@fluentui/react-icons";
import ChatMessage from "./ChatMessage";
import TelemetryFooter from "./TelemetryFooter";
import { useChatSession } from "../hooks/useChatSession";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },

  // ── Email context bar ──
  emailBar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 16px",
    backgroundColor: "var(--bg-secondary, #F8F9FA)",
    borderBottom: "1px solid var(--border-primary, #E0E2E6)",
    flexShrink: 0,
    minHeight: "36px",
  },
  emailIcon: {
    fontSize: "13px",
    flexShrink: 0,
  },
  emailSubject: {
    fontSize: "12px",
    fontWeight: "500",
    color: "var(--text-secondary, #4A4F57)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    minWidth: 0,
  },
  emailFrom: {
    fontSize: "11px",
    color: "var(--text-tertiary, #6E747D)",
    flexShrink: 0,
  },

  // ── Messages area ──
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    paddingTop: "8px",
    paddingBottom: "8px",
  },

  // ── Welcome state ──
  welcomeState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    padding: "24px 20px",
    textAlign: "center",
    gap: "12px",
  },
  welcomeIcon: {
    width: "48px",
    height: "48px",
    opacity: 0.6,
  },
  welcomeTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "var(--text-primary, #0B111A)",
  },
  welcomeSubtitle: {
    fontSize: "13px",
    color: "var(--text-tertiary, #6E747D)",
    maxWidth: "260px",
    lineHeight: "1.5",
  },
  suggestionsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
    width: "100%",
    marginTop: "8px",
  },
  suggestionBtn: {
    fontSize: "12px",
    padding: "10px 8px",
    borderRadius: "10px",
    border: "1px solid var(--border-primary, #E0E2E6)",
    backgroundColor: "var(--bg-elevated, #FFFFFF)",
    color: "var(--text-secondary, #4A4F57)",
    cursor: "pointer",
    textAlign: "left",
    fontWeight: "500",
    transition: "all 0.15s ease",
    lineHeight: "1.35",
    minHeight: "44px",
    display: "flex",
    alignItems: "center",
    ":hover": {
      backgroundColor: "var(--bg-active, #FFF5D6)",
      borderColor: "#FFB500",
      color: "#0B111A",
    },
  },

  // ── Input area ──
  inputArea: {
    display: "flex",
    alignItems: "flex-end",
    gap: "8px",
    padding: "8px 12px 12px 12px",
    borderTop: "1px solid var(--border-primary, #E0E2E6)",
    backgroundColor: "var(--bg-primary, #FFFFFF)",
    flexShrink: 0,
  },
  inputField: {
    flex: 1,
    "& input": {
      fontSize: "13px",
    },
  },
  sendBtn: {
    minWidth: "36px",
    width: "36px",
    height: "36px",
    padding: "0",
    borderRadius: "50%",
    backgroundColor: "#FFB500",
    color: "#0B111A",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background-color 0.15s ease",
    ":hover": {
      backgroundColor: "#E6A300",
    },
    ":disabled": {
      opacity: 0.4,
      cursor: "not-allowed",
    },
  },
  clearBtn: {
    minWidth: "32px",
    width: "32px",
    height: "32px",
    padding: "0",
    flexShrink: 0,
  },
});

const SUGGESTIONS = [
  { text: "Summarize this email", icon: "" },
  { text: "What are the action items?", icon: "" },
  { text: "Draft a reply", icon: "" },
  { text: "Translate to...", icon: "" },
];

/**
 * ChatView — the main chat interface replacing the button-based UI.
 *
 * Layout:
 *   [Email context bar]
 *   [Messages / Welcome]
 *   [Input bar]
 *
 * @param {{
 *   email: Object|null,
 *   config: Object,
 *   emailLoading: boolean,
 *   emailError: string|null,
 * }} props
 */
const ChatView = ({ email, config, emailLoading, emailError }) => {
  const styles = useStyles();
  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const [inputValue, setInputValue] = React.useState("");

  const {
    messages,
    loading,
    actionStates,
    sendMessage,
    selectOption,
    executeActionItem,
    cancel,
    clearChat,
  } = useChatSession({ email, config });

  // Auto-scroll to bottom when new messages arrive
  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Focus input on mount
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text || loading) return;
    setInputValue("");
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (text) => {
    sendMessage(text);
  };

  const handleOptionSelect = (label) => {
    selectOption(label);
  };

  // Get last telemetry from messages
  const lastTelemetry = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].telemetry) return messages[i].telemetry;
    }
    return null;
  }, [messages]);

  const hasMessages = messages.length > 0;

  return (
    <div className={styles.root}>
      {/* ── Email context bar ── */}
      {email && (
        <div className={styles.emailBar}>
          <span className={styles.emailIcon}>&#x2709;</span>
          <Text className={styles.emailSubject}>
            {email.subject || "(No subject)"}
          </Text>
          <Text className={styles.emailFrom}>
            {email.from?.name || email.from?.email || ""}
          </Text>
        </div>
      )}

      {/* ── Messages area ── */}
      <div className={styles.messagesArea}>
        {!hasMessages && (
          <div className={styles.welcomeState}>
            <img src="../../../assets/apple-touch-icon.png" className={styles.welcomeIcon} alt="IAN" />
            <Text className={styles.welcomeTitle}>Chat with IAN</Text>
            <Text className={styles.welcomeSubtitle}>
              {email
                ? "Ask me to summarize, translate, reply, or do anything with this email."
                : "Open an email and I'll be ready to help you."}
            </Text>

            {/* Suggestion buttons */}
            {email && (
              <div className={styles.suggestionsGrid}>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s.text}
                    className={styles.suggestionBtn}
                    onClick={() => handleSuggestionClick(s.text)}
                    type="button"
                  >
                    {s.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {hasMessages &&
          messages.map((msg) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              onOptionSelect={handleOptionSelect}
              onExecuteAction={executeActionItem}
              actionStates={actionStates}
              emailContext={email ? { id: email.id, subject: email.subject, from: email.from } : {}}
            />
          ))}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* ── Telemetry (last response) ── */}
      {lastTelemetry && <TelemetryFooter telemetry={lastTelemetry} />}

      {/* ── Input area ── */}
      <div className={styles.inputArea}>
        {/* Clear chat button */}
        {hasMessages && (
          <Button
            className={styles.clearBtn}
            appearance="subtle"
            icon={<Delete24Regular />}
            onClick={clearChat}
            title="Clear chat"
            size="small"
          />
        )}

        <Input
          ref={inputRef}
          className={styles.inputField}
          value={inputValue}
          onChange={(_, data) => setInputValue(data.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            loading
              ? "Processing..."
              : email
                ? "Ask IAN anything about this email..."
                : "Open an email first..."
          }
          disabled={loading || !email}
          size="medium"
          appearance="outline"
        />

        <button
          className={styles.sendBtn}
          onClick={loading ? cancel : handleSend}
          disabled={!loading && (!inputValue.trim() || !email)}
          title={loading ? "Cancel" : "Send"}
          type="button"
        >
          {loading ? (
            <Dismiss16Regular />
          ) : (
            <Send24Filled style={{ fontSize: "18px" }} />
          )}
        </button>
      </div>
    </div>
  );
};

export default ChatView;
